/**
 * File service for API communication
 */

import { 
  File, 
  FileListResponse, 
  FileSearchRequest, 
  CreateFolderRequest, 
  CreateFileRequest,
  UpdateFileRequest,
  ShareFileRequest,
  CreatePublicLinkRequest,
  FileUploadResponse,
  FileBreadcrumb,
  FileOCR,
  FileClassification 
} from '../types/files';

// Check if we should use real API or mock data
const USE_REAL_API = import.meta.env.MODE === 'production' || import.meta.env.VITE_USE_REAL_API === 'true';
const API_BASE_URL = import.meta.env.VITE_API_URL;

class FileService {
  private baseUrl = `${API_BASE_URL}/files`;

  // Mock data for development
  private mockFiles: File[] = [
    {
      id: '00000000-0000-0000-0000-000000000001',
      name: 'Documents',
      originalName: 'Documents',
      path: '/documents',
      mimeType: 'application/folder',
      size: 0,
      type: 'folder',
      tags: ['important'],
      permissions: {
        owner: 'user-1',
        organizationId: 'org-1',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'user-1'
    },
    {
      id: '00000000-0000-0000-0000-000000000002',
      name: 'loan-application.pdf',
      originalName: 'loan-application.pdf',
      path: '/documents/loan-application.pdf',
      mimeType: 'application/pdf',
      size: 1024000,
      folderId: '00000000-0000-0000-0000-000000000001',
      type: 'file',
      tags: ['loan', 'application'],
      classification: {
        category: 'financial',
        confidence: 0.95,
        autoClassified: true
      },
      ocr: {
        text: 'Loan Application Form\nApplicant Name: John Doe\nLoan Amount: $50,000\nBusiness Type: LLC',
        confidence: 0.92,
        processedAt: new Date().toISOString(),
        status: 'completed'
      },
      sharing: {
        isShared: false,
        sharedWith: []
      },
      metadata: {
        clientId: 'client-1',
        uploadedVia: 'web'
      },
      permissions: {
        owner: 'user-1',
        organizationId: 'org-1',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'user-1'
    },
    {
      id: '00000000-0000-0000-0000-000000000003',
      name: 'bank-statement-march.pdf',
      originalName: 'bank-statement-march.pdf',
      path: '/documents/bank-statement-march.pdf',
      mimeType: 'application/pdf',
      size: 856000,
      folderId: '00000000-0000-0000-0000-000000000001',
      type: 'file',
      tags: ['bank', 'statement', 'march'],
      classification: {
        category: 'bank_statement',
        confidence: 0.98,
        autoClassified: true
      },
      ocr: {
        text: 'Bank Statement\nAccount: 1234567890\nPeriod: March 2024\nBeginning Balance: $25,000\nEnding Balance: $32,000',
        confidence: 0.89,
        processedAt: new Date().toISOString(),
        status: 'completed'
      },
      sharing: {
        isShared: true,
        sharedWith: [
          {
            userId: 'user-2',
            permission: 'read',
            sharedAt: new Date().toISOString(),
            sharedBy: 'user-1'
          }
        ]
      },
      metadata: {
        clientId: 'client-1',
        uploadedVia: 'web'
      },
      permissions: {
        owner: 'user-1',
        organizationId: 'org-1',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'user-1'
    },
    {
      id: '00000000-0000-0000-0000-000000000004',
      name: 'Financial Reports',
      originalName: 'Financial Reports',
      path: '/financial-reports',
      mimeType: 'application/folder',
      size: 0,
      type: 'folder',
      tags: ['financial', 'reports'],
      permissions: {
        owner: 'user-1',
        organizationId: 'org-1',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'user-1'
    },
    {
      id: '00000000-0000-0000-0000-000000000005',
      name: 'tax-return-2023.pdf',
      originalName: 'tax-return-2023.pdf',
      path: '/documents/tax-return-2023.pdf',
      mimeType: 'application/pdf',
      size: 2048000,
      folderId: '00000000-0000-0000-0000-000000000001',
      type: 'file',
      tags: ['tax', '2023', 'return'],
      classification: {
        category: 'tax',
        confidence: 0.93,
        autoClassified: true
      },
      ocr: {
        text: 'Form 1040\nTax Year 2023\nTaxpayer: John Doe\nTotal Income: $85,000',
        confidence: 0.87,
        processedAt: new Date().toISOString(),
        status: 'completed'
      },
      sharing: {
        isShared: false,
        sharedWith: []
      },
      metadata: {
        clientId: 'client-1',
        uploadedVia: 'web'
      },
      permissions: {
        owner: 'user-1',
        organizationId: 'org-1',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'user-1'
    }
  ];

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    if (!USE_REAL_API) {
      // Return mock data for development
      return this.getMockResponse<T>(url, options);
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  private async getMockResponse<T>(url: string, options: RequestInit): Promise<T> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 200));

    const method = options.method || 'GET';
    const urlObj = new URL(url, this.baseUrl);

    if (method === 'GET' && url.includes('/files')) {
      return this.getMockFileList(urlObj) as T;
    }

    if (method === 'POST' && url.includes('/folders')) {
      const body = JSON.parse(options.body as string);
      return this.createMockFolder(body) as T;
    }

    if (method === 'POST' && url.includes('/upload')) {
      const body = JSON.parse(options.body as string);
      return this.createMockFile(body) as T;
    }

    // Default mock response
    return {} as T;
  }

  private getMockFileList(url: URL): FileListResponse {
    const params = url.searchParams;
    const folderId = params.get('folderId');
    const query = params.get('query');
    const limit = parseInt(params.get('limit') || '20');
    const offset = parseInt(params.get('offset') || '0');

    let filteredFiles = this.mockFiles.filter(file => !file.isDeleted);

    // Filter by folder
    if (folderId !== null) {
      filteredFiles = filteredFiles.filter(file => file.folderId === folderId);
    } else {
      // Show root level folders only
      filteredFiles = filteredFiles.filter(file => !file.folderId && file.type === 'folder');
    }

    // Search by name
    if (query) {
      const queryLower = query.toLowerCase();
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(queryLower) ||
        file.tags.some(tag => tag.toLowerCase().includes(queryLower))
      );
    }

    // Sort by creation date (newest first)
    filteredFiles.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Pagination
    const paginatedFiles = filteredFiles.slice(offset, offset + limit);

    return {
      files: paginatedFiles,
      total: filteredFiles.length,
      hasMore: offset + limit < filteredFiles.length
    };
  }

  private createMockFolder(data: CreateFolderRequest): File {
    const newFolder: File = {
      id: `mock-folder-${Date.now()}`,
      name: data.name,
      originalName: data.name,
      path: `/${data.name}`,
      mimeType: 'application/folder',
      size: 0,
      folderId: data.parentId,
      type: 'folder',
      tags: [],
      metadata: data.metadata,
      permissions: {
        owner: 'current-user',
        organizationId: 'current-org',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user'
    };

    this.mockFiles.push(newFolder);
    return newFolder;
  }

  private createMockFile(data: CreateFileRequest): FileUploadResponse {
    const newFile: File = {
      id: `mock-file-${Date.now()}`,
      name: data.name,
      originalName: data.originalName,
      path: `/${data.name}`,
      mimeType: data.mimeType,
      size: data.size,
      folderId: data.folderId,
      type: 'file',
      tags: data.tags || [],
      metadata: data.metadata,
      permissions: {
        owner: 'current-user',
        organizationId: 'current-org',
        access: [
          { type: 'role', id: 'admin', permission: 'admin' },
          { type: 'role', id: 'manager', permission: 'write' }
        ]
      },
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user'
    };

    this.mockFiles.push(newFile);

    return {
      file: newFile,
      uploadUrl: `/api/v1/files/${newFile.id}/upload`
    };
  }

  // Public API methods
  async getFiles(request: FileSearchRequest = {}): Promise<FileListResponse> {
    const params = new URLSearchParams();
    
    if (request.query) params.append('query', request.query);
    if (request.folderId !== undefined) params.append('folderId', request.folderId);
    if (request.tags) params.append('tags', request.tags.join(','));
    if (request.classification) params.append('classification', request.classification);
    if (request.mimeType) params.append('mimeType', request.mimeType);
    if (request.createdBy) params.append('createdBy', request.createdBy);
    if (request.dateFrom) params.append('dateFrom', request.dateFrom);
    if (request.dateTo) params.append('dateTo', request.dateTo);
    if (request.limit) params.append('limit', request.limit.toString());
    if (request.offset) params.append('offset', request.offset.toString());

    return this.makeRequest<FileListResponse>(`${this.baseUrl}?${params}`);
  }

  async getFile(id: string): Promise<File> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id && !f.isDeleted);
      if (!file) throw new Error('File not found');
      return file;
    }
    return this.makeRequest<File>(`${this.baseUrl}/${id}`);
  }

  async createFolder(request: CreateFolderRequest): Promise<File> {
    return this.makeRequest<File>(`${this.baseUrl}/folders`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async uploadFile(request: CreateFileRequest): Promise<FileUploadResponse> {
    return this.makeRequest<FileUploadResponse>(`${this.baseUrl}/upload`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async updateFile(id: string, updates: UpdateFileRequest): Promise<File> {
    if (!USE_REAL_API) {
      const fileIndex = this.mockFiles.findIndex(f => f.id === id);
      if (fileIndex === -1) throw new Error('File not found');
      
      this.mockFiles[fileIndex] = {
        ...this.mockFiles[fileIndex],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      return this.mockFiles[fileIndex];
    }

    return this.makeRequest<File>(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFile(id: string): Promise<void> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id);
      if (file) {
        file.isDeleted = true;
        file.deletedAt = new Date().toISOString();
      }
      return;
    }

    await this.makeRequest(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
  }

  async shareFile(id: string, request: ShareFileRequest): Promise<void> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id);
      if (file) {
        if (!file.sharing) {
          file.sharing = { isShared: false, sharedWith: [] };
        }
        
        for (const userId of request.userIds) {
          const existingShare = file.sharing.sharedWith.find(s => s.userId === userId);
          if (existingShare) {
            existingShare.permission = request.permission;
          } else {
            file.sharing.sharedWith.push({
              userId,
              permission: request.permission,
              sharedAt: new Date().toISOString(),
              sharedBy: 'current-user'
            });
          }
        }
        
        file.sharing.isShared = file.sharing.sharedWith.length > 0;
      }
      return;
    }

    await this.makeRequest(`${this.baseUrl}/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async createPublicLink(id: string, request: CreatePublicLinkRequest): Promise<{ token: string; url: string }> {
    if (!USE_REAL_API) {
      const token = `mock-token-${Date.now()}`;
      return {
        token,
        url: `${window.location.origin}/shared/${token}`
      };
    }

    return this.makeRequest(`${this.baseUrl}/${id}/public-link`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getBreadcrumbs(id: string): Promise<FileBreadcrumb[]> {
    if (!USE_REAL_API) {
      const breadcrumbs: FileBreadcrumb[] = [];
      let currentFile = this.mockFiles.find(f => f.id === id);

      while (currentFile && currentFile.folderId) {
        const parentFolder = this.mockFiles.find(f => f.id === currentFile!.folderId);
        if (parentFolder && parentFolder.type === 'folder') {
          breadcrumbs.unshift({
            id: parentFolder.id,
            name: parentFolder.name,
            type: 'folder'
          });
          currentFile = parentFolder;
        } else {
          break;
        }
      }

      return breadcrumbs;
    }

    return this.makeRequest<FileBreadcrumb[]>(`${this.baseUrl}/${id}/breadcrumbs`);
  }

  async downloadFile(id: string): Promise<{ downloadUrl: string; fileName: string; mimeType: string; size: number }> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id && !f.isDeleted);
      if (!file) throw new Error('File not found');
      
      return {
        downloadUrl: `/api/v1/files/${id}/content`,
        fileName: file.originalName,
        mimeType: file.mimeType,
        size: file.size
      };
    }

    return this.makeRequest(`${this.baseUrl}/${id}/download`);
  }

  async processOCR(id: string): Promise<FileOCR> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id);
      if (!file) throw new Error('File not found');

      const ocrResult: FileOCR = {
        text: `OCR processed text for ${file.name}`,
        confidence: 0.85 + Math.random() * 0.1,
        processedAt: new Date().toISOString(),
        status: 'completed'
      };

      // Update the file with OCR results
      file.ocr = ocrResult;
      return ocrResult;
    }

    return this.makeRequest<FileOCR>(`${this.baseUrl}/${id}/ocr`, {
      method: 'POST',
    });
  }

  async classifyFile(id: string): Promise<FileClassification> {
    if (!USE_REAL_API) {
      const file = this.mockFiles.find(f => f.id === id);
      if (!file) throw new Error('File not found');

      // Mock classification based on file name
      let category: FileClassification['category'] = 'other';
      const fileName = file.name.toLowerCase();
      
      if (fileName.includes('bank') || fileName.includes('statement')) {
        category = 'bank_statement';
      } else if (fileName.includes('invoice')) {
        category = 'invoice';
      } else if (fileName.includes('contract')) {
        category = 'contract';
      } else if (fileName.includes('tax')) {
        category = 'tax';
      } else if (fileName.includes('loan') || fileName.includes('financial')) {
        category = 'financial';
      } else if (fileName.includes('legal')) {
        category = 'legal';
      } else if (fileName.includes('identity') || fileName.includes('id')) {
        category = 'identity';
      } else {
        category = 'document';
      }

      const classification: FileClassification = {
        category,
        confidence: 0.8 + Math.random() * 0.15,
        autoClassified: true
      };

      // Update the file with classification
      file.classification = classification;
      return classification;
    }

    return this.makeRequest<FileClassification>(`${this.baseUrl}/${id}/classify`, {
      method: 'POST',
    });
  }
}

export const fileService = new FileService();